import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20airy%20modern%20retail%20display%20with%20stylish%20colorful%20sneakers%20on%20transparent%20acrylic%20shelves%2C%20vibrant%20product%20photography%20with%20neon%20accent%20lighting%2C%20bold%20contemporary%20store%20interior%2C%20energetic%20dynamic%20composition%2C%20geometric%20shapes%20and%20clean%20lines%2C%20high-end%20fashion%20retail%20atmosphere&width=1600&height=900&seq=litbuy-hero-v2-001&orientation=landscape"
          alt="LitBuy Spreadsheet - modern retail product showcase"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground-950/50 via-foreground-950/35 to-foreground-950/55"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="max-w-[640px]">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></span>
            <span className="text-xs font-medium text-background-50 tracking-wide uppercase">The #1 LitBuy Spreadsheet Resource</span>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 leading-[1.05] mb-6">
            Track Every Deal<br />
            <span className="text-primary-400">With LitBuy</span> Spreadsheet
          </h1>

          <p className="text-base md:text-lg text-background-200/90 max-w-[480px] leading-relaxed mb-8">
            Your ultimate resource hub for LitBuy Spreadsheet — discover guides, templates, and tools to track deals across Shoes, Hoodies, T-Shirts, Jackets, and 10 more shopping categories with ease.
          </p>

          {/* CTA Buttons + Dropdown */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-external-link-line"></i>
              LitBuy Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 rounded-full bg-background-50 text-foreground-900 font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-shopping-bag-line"></i>
              Buy It Now
            </a>

            {/* Dropdown in Hero */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-6 py-3 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/25 text-background-50 font-semibold text-sm hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-grid-line"></i>
                Shop Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 shadow-xl py-2 z-20 max-h-[380px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-3 whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-6 mt-10 text-background-200/70">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-accent-400"></i>
              <span className="text-xs">Trusted Resource</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-guide-line text-accent-400"></i>
              <span className="text-xs">In-Depth Guides</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-global-line text-accent-400"></i>
              <span className="text-xs">14+ Categories</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/15 border border-background-50/20 cursor-pointer">
          <i className="ri-arrow-down-line text-background-50 text-sm"></i>
        </div>
      </div>
    </section>
  );
}